import React from "react";
import {
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Divider,
} from "@mui/material";
import { StyledCard } from "../common/StyledCard";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import LayersIcon from "@mui/icons-material/Layers";
import { format } from "date-fns/format";
import StatusChip from "../common/StatusChip";
import { AuditLogResponseDto } from "../../shared/dtos/auditLog/AuditLogResponseDto";

interface AuditLogCardProps {
  auditLog: AuditLogResponseDto;
  onDelete?: (id: number) => void;
  canDelete?: boolean;
}

const AuditLogCard: React.FC<AuditLogCardProps> = ({
  auditLog,
  onDelete,
  canDelete,
}) => {
  return (
    <StyledCard>
      <Box
        sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%" }}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <StatusChip status={auditLog.actionType} />
            <Typography
              variant="caption"
              sx={{ fontWeight: "bold", color: "text.secondary" }}
            >
              ID: #{auditLog.id}
            </Typography>
          </Stack>

          {canDelete && onDelete && (
            <Tooltip title="Delete Log Entry">
              <IconButton
                size="small"
                color="error"
                onClick={() => onDelete(auditLog.id)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ flexGrow: 1, my: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Monospace",
              p: 1,
              bgcolor: "action.hover",
              borderRadius: 1,
              borderLeft: "3px solid",
              borderColor: "divider",
              fontSize: "0.8rem",
            }}
          >
            {auditLog.description}
          </Typography>
        </Box>

        <Stack spacing={0.8} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <LayersIcon sx={{ fontSize: "0.9rem" }} color="action" />
            <Typography variant="caption" color="text.secondary">
              <b>Entity:</b> {auditLog.entityType}{" "}
              {auditLog.branchName ? `(${auditLog.branchName})` : ""}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <PersonIcon sx={{ fontSize: "0.9rem" }} color="action" />
            <Typography variant="caption" color="text.secondary">
              <b>User:</b> {auditLog.userDetails}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <AccessTimeIcon sx={{ fontSize: "0.9rem" }} color="action" />
            <Typography variant="caption" color="text.secondary">
              {format(new Date(auditLog.timestamp), "PPp")}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </StyledCard>
  );
};

export default AuditLogCard;
